import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSettings, getGarages, PLAN_LIMITS, syncSubscriptionFromSupabase, getOwnedVehicleCount } from '../utils/storage';
import { redirectToCheckout } from '../services/stripeService';
import { isNativeApp, isIOS, isAndroid, getAccountUrl } from '../utils/platform';
import { isIAPAvailable, purchaseSubscription } from '../services/iapService';
import { supabase } from '../config/supabase';
import AppUpgradeNotice from './AppUpgradeNotice';
import LegalModal from './LegalModal';
import { isNorthAmericaLocale } from '../utils/regionDefaults';

/* ─── Tier definitions ─── */

const TIERS = [
  {
    key: 'free',
    name: 'Free',
    price: '$0',
    iapPrice: '$0',
    period: 'forever',
    features: ['1 vehicle', '1 garage', '15 AI credits/mo'],
  },
  {
    key: 'enthusiast',
    name: 'Enthusiast',
    price: '$9.99',
    iapPrice: '$9.99',
    period: '/month',
    features: ['5 vehicles', '1 garage', '100 AI credits/mo', 'Route builder'],
    accent: true,
    recommended: true,
  },
  {
    key: 'collector',
    name: 'Collector',
    price: '$19.99',
    iapPrice: '$19.99',
    period: '/month',
    features: ['15 vehicles', '3 garages', '250 AI credits/mo', 'Route builder'],
  },
  {
    key: 'showroom',
    name: 'Showroom',
    price: '$39.99',
    iapPrice: '$39.99',
    period: '/month',
    features: ['50 vehicles', '5 garages', '500 AI credits/mo', 'Route builder'],
  },
  {
    key: 'team',
    name: 'Team',
    price: '$99.99',
    iapPrice: '$99.99',
    period: '/month',
    features: ['Unlimited vehicles', 'Unlimited garages', 'Unlimited collaborators', '500 AI credits/mo', 'Route builder'],
  },
];

/* ─── Tier ranking for downgrade detection ─── */

const TIER_RANK = { free: 0, enthusiast: 1, collector: 2, showroom: 3, team: 4 };

/* ─── Component ─── */

export default function PricingModal({ onClose, onToast, title, subtitle }) {
  // All hooks MUST be above any early returns (React rules of hooks)
  const { user, demoVariant } = useAuth();
  const [loadingTier, setLoadingTier] = useState(null);
  const [showLegal, setShowLegal] = useState(null); // 'terms' | 'privacy' | null
  const [downgradeBlock, setDowngradeBlock] = useState(null); // { tierName, issues[] }

  // If iOS native app but IAP bridge is not loaded, show fallback.
  // Android native app skips this — falls through to Stripe checkout like web.
  if (isIOS && !isIAPAvailable()) {
    return <AppUpgradeNotice onClose={onClose} title={title} subtitle={subtitle} />;
  }

  const settings = getSettings();
  const currentPlan = settings.subscription.plan;
  const isPaid = currentPlan !== 'free';
  const useIAP = isNativeApp && isIAPAvailable();
  // Our Android wrapper sells nothing in-app (Google Play policy). Hide the
  // plans/checkout and send the user to the web to manage their subscription.
  const androidManageOnWeb = isAndroid;

  // Region gate for paid tiers. iOS is unaffected — Apple is merchant of
  // record via IAP and handles VAT for us. Web + Android Stripe purchases
  // would make Proper Miles the merchant for direct B2C SaaS to UK / EU
  // consumers, which triggers VAT registration. Until we either turn on
  // Stripe Tax (with our own VAT registration) or move web/Android B2C to
  // a Merchant-of-Record provider like Paddle, we simply don't sell paid
  // plans to non-NA consumers via Stripe.
  const paidPlansAvailable = useIAP || isNorthAmericaLocale();

  /**
   * Check if switching to targetTier would exceed its limits.
   * Returns an array of human-readable issues, or empty array if OK.
   */
  function checkDowngradeLimits(targetTierKey) {
    const targetLimits = PLAN_LIMITS[targetTierKey] || PLAN_LIMITS.free;
    // Only count OWNED vehicles/garages (exclude shared/collaborator)
    const currentVehicles = getOwnedVehicleCount();
    const currentGarages = getGarages().filter(g => g.role !== 'collaborator').length;
    const issues = [];

    // Vehicle over-limit
    if (targetLimits.maxVehicles !== Infinity && currentVehicles > targetLimits.maxVehicles) {
      const over = currentVehicles - targetLimits.maxVehicles;
      issues.push(`Delete ${over} vehicle${over > 1 ? 's' : ''} (you have ${currentVehicles}, plan allows ${targetLimits.maxVehicles})`);
    }

    // Garage over-limit
    if (targetLimits.maxGarages !== Infinity && currentGarages > targetLimits.maxGarages) {
      const over = currentGarages - targetLimits.maxGarages;
      issues.push(`Delete ${over} garage${over > 1 ? 's' : ''} (you have ${currentGarages}, plan allows ${targetLimits.maxGarages})`);
    }

    // Sharing — if current plan allows sharing but target doesn't
    if (!targetLimits.canShare) {
      const allGarages = getGarages();
      const hasCollaborators = allGarages.some(
        (g) => g.collaborators && g.collaborators.length > 0
      );
      if (hasCollaborators) {
        issues.push('Remove all collaborators from your garages');
      }
    }

    return issues;
  }

  function handleSelectTier(tierKey) {
    if (tierKey === currentPlan) return;

    const isDowngrade = (TIER_RANK[tierKey] ?? 0) < (TIER_RANK[currentPlan] ?? 0);

    if (isDowngrade) {
      const issues = checkDowngradeLimits(tierKey);
      if (issues.length > 0) {
        const tierName = TIERS.find((t) => t.key === tierKey)?.name || tierKey;
        setDowngradeBlock({ tierName, issues });
        return;
      }
    }

    // Free downgrade with no blockers — just update locally
    if (tierKey === 'free') {
      handleDowngradeToFree();
      return;
    }

    // Paid tier switch — proceed to payment
    handleUpgrade(tierKey);
  }

  function handleDowngradeToFree() {
    // On native: user must cancel via App Store / Google Play
    if (useIAP) {
      onToast?.('To cancel your subscription, go to Settings > Manage Subscription in the App Store.');
      return;
    }
    // On web: redirect to Stripe Portal where they can cancel
    import('../services/stripeService').then(({ redirectToPortal }) => {
      redirectToPortal(user?.email, user?.id).catch((err) => {
        // No Stripe customer = subscription was created via IAP, not Stripe
        if (err?.message?.includes('No Stripe customer')) {
          onToast?.('Your subscription was created through the App Store. To change your subscription, please use the mobile app.');
        } else {
          onToast?.('Unable to open billing portal. Please try again.');
        }
      });
    });
  }

  async function handleUpgrade(tierKey) {
    setLoadingTier(tierKey);

    try {
      if (useIAP) {
        // Native app: trigger Apple/Google purchase sheet
        if (import.meta.env.DEV) console.log('[Pricing] Starting IAP for tier:', tierKey, 'user:', user?.id);
        const result = await purchaseSubscription(tierKey, user.id);

        if (result.status === 'completed') {
          // Optimistically update localStorage NOW — we know the tier the user bought.
          // The iap-verify edge function may still be writing to Supabase, so polling
          // immediately would read the old tier (race condition).
          syncSubscriptionFromSupabase({
            subscription_tier: tierKey,
            credits_remaining: null, // let syncSubscriptionFromSupabase use tier defaults
          });

          // Also poll Supabase after a delay as a backup to get the real credit count
          setTimeout(async () => {
            try {
              const { data: profile } = await supabase
                .from('users')
                .select('subscription_tier, credits_remaining')
                .eq('id', user.id)
                .maybeSingle();
              if (profile) syncSubscriptionFromSupabase(profile);
            } catch { /* will sync on next app focus */ }
          }, 3000);

          onToast(`Upgraded to ${tierKey.charAt(0).toUpperCase() + tierKey.slice(1)}!`);
          onClose();
          return;
        } else if (result.status === 'canceled') {
          // User dismissed purchase sheet — no error
          setLoadingTier(null);
          return;
        } else {
          // Purchase dismissed or failed — reset silently
          setLoadingTier(null);
          return;
        }
      } else {
        // Web: redirect to Stripe checkout
        if (import.meta.env.DEV) console.log('[Pricing] Starting checkout for tier:', tierKey, 'user:', user?.id, user?.email);
        await redirectToCheckout(tierKey, user.email, user.id);
        // User will be redirected away — no further code runs
      }
    } catch (err) {
      console.error('[Pricing] Upgrade error:', err.message || err);
      if (import.meta.env.DEV) console.error('[Pricing] Full error:', err);
      const isTimeout = err.message?.includes('timed out');
      if (!isTimeout) {
        onToast(err.message || 'Unable to complete upgrade. Please try again.');
      }
      setLoadingTier(null);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
          padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 4 }}>
            {title || 'Choose Your Plan'}
          </h2>
          <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {subtitle || 'Unlock more vehicles, garages, and AI credits.'}
          </p>
        </div>

        {androidManageOnWeb && (
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 16 }}>
              Manage your Proper Miles subscription on our website — open it in
              your browser to view your plan, upgrade, or update payment details.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => {
                const url = getAccountUrl();
                if (typeof window !== 'undefined' && window.__pmOpenExternal) window.__pmOpenExternal(url);
                else window.open(url, '_blank', 'noopener,noreferrer');
              }}
              style={{ width: '100%' }}
            >
              Open in browser
            </button>
          </div>
        )}

        {!androidManageOnWeb && (<>
        {/* Tier cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {TIERS.map((tier) => {
            const isCurrent = tier.key === currentPlan;
            const isLoading = loadingTier === tier.key;
            const isHighlighted = isCurrent;
            const displayPrice = useIAP ? tier.iapPrice : tier.price;
            const isDowngrade = isPaid && tier.key === 'free';

            return (
              <div
                key={tier.key}
                style={{
                  border: `1.5px solid ${isHighlighted ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '14px 16px',
                  background: isHighlighted ? 'var(--accent-subtle)' : 'var(--bg-input)',
                  position: 'relative',
                }}
              >
                {/* Tier header: name + badges + price */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{tier.name}</span>
                    {isCurrent && (
                      <span style={{
                        fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 4,
                        background: 'var(--accent)', color: '#fff',
                      }}>
                        CURRENT
                      </span>
                    )}
                    {tier.recommended && !isCurrent && (
                      <span style={{
                        fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 4,
                        background: 'rgba(211,71,74,0.15)', color: 'var(--accent)',
                      }}>
                        RECOMMENDED
                      </span>
                    )}
                  </div>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>{displayPrice}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{tier.period}</span>
                  </div>
                </div>

                {/* Features list */}
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {tier.features.filter(f => !(f === 'Route builder' && demoVariant === 'alex')).map((f, i) => (
                    <li key={i} style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Action button */}
                {isCurrent ? (
                  <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', fontWeight: 600 }}>
                    Current Plan
                  </div>
                ) : isDowngrade ? (
                  /* Downgrade to Free button — only shown for paid users */
                  <button
                    className="btn btn-secondary"
                    style={{
                      marginTop: 10, padding: '10px 16px', fontSize: '0.8125rem',
                      color: 'var(--text-muted)',
                    }}
                    onClick={() => handleSelectTier('free')}
                    disabled={!!loadingTier}
                  >
                    Downgrade to Free
                  </button>
                ) : tier.key !== 'free' ? (
                  paidPlansAvailable ? (
                    <button
                      className={`btn ${tier.accent ? 'btn-primary' : 'btn-secondary'}`}
                      style={{
                        marginTop: 10, padding: '10px 16px', fontSize: '0.8125rem',
                        opacity: loadingTier ? 0.6 : 1,
                        cursor: loadingTier ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => handleSelectTier(tier.key)}
                      disabled={!!loadingTier}
                    >
                      {isLoading
                        ? (useIAP ? 'Processing…' : 'Redirecting to checkout…')
                        : isPaid
                          ? `Switch to ${tier.name}`
                          : `Subscribe to ${tier.name}`}
                    </button>
                  ) : (
                    /* Region-gated: paid Stripe tiers not yet available outside NA */
                    <div
                      style={{
                        marginTop: 10, padding: '10px 16px', fontSize: '0.8125rem',
                        textAlign: 'center', fontWeight: 600,
                        background: 'rgba(200,200,200,0.08)',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      Coming soon to your region
                    </div>
                  )
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Auto-renewal disclosure — required by Apple guideline 3.1.2 */}
        <div style={{
          textAlign: 'center',
          marginBottom: 8,
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}>
          Subscription automatically renews unless cancelled at least 24 hours
          before the end of the current period. Manage or cancel anytime in Settings.
        </div>

        {/* Legal links — required by Apple guideline 3.1.2 */}
        <div style={{
          textAlign: 'center',
          marginBottom: 12,
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          lineHeight: 1.5,
        }}>
          By subscribing, you agree to our{' '}
          <button
            onClick={() => setShowLegal('terms')}
            style={{
              color: 'var(--accent)', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: '0.6875rem', textDecoration: 'underline',
              padding: 0, fontFamily: 'inherit',
            }}
          >
            Terms of Use
          </button>
          ,{' '}
          <button
            onClick={() => setShowLegal('privacy')}
            style={{
              color: 'var(--accent)', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: '0.6875rem', textDecoration: 'underline',
              padding: 0, fontFamily: 'inherit',
            }}
          >
            Privacy Policy
          </button>
          , and Apple's{' '}
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--accent)', fontSize: '0.6875rem', textDecoration: 'underline',
            }}
          >
            Standard EULA
          </a>
        </div>
        </>)}

        <button className="btn btn-ghost" onClick={onClose} style={{ color: 'var(--text-muted)' }}>
          Maybe Later
        </button>
      </div>

      {/* Downgrade blocked overlay */}
      {downgradeBlock && (
        <div
          onClick={() => setDowngradeBlock(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 210, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-md)',
              padding: 24,
              width: '100%',
              maxWidth: 340,
              border: '1px solid var(--border)',
            }}
          >
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 6 }}>
              Before switching to {downgradeBlock.tierName}...
            </h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
              Your current usage exceeds the {downgradeBlock.tierName} plan limits. Please make these changes first:
            </p>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', marginBottom: 20 }}>
              {downgradeBlock.issues.map((issue, i) => (
                <li
                  key={i}
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--text-primary)',
                    marginBottom: 8,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'flex-start',
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ color: 'var(--danger)', fontWeight: 700, flexShrink: 0 }}>•</span>
                  {issue}
                </li>
              ))}
            </ul>
            <button
              className="btn btn-primary"
              onClick={() => setDowngradeBlock(null)}
              style={{ fontSize: '0.8125rem' }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Legal modal */}
      {showLegal && (
        <LegalModal
          type={showLegal}
          onClose={() => setShowLegal(null)}
        />
      )}
    </div>
  );
}
